/**
 * Google Analytics 4 Service
 * Handles analytics tracking with built-in consent management
 */

// Google Analytics 4 Measurement ID
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Analytics consent storage key
const ANALYTICS_CONSENT_KEY = 'portfolio_analytics_consent';

/**
 * Analytics consent status
 */
export interface AnalyticsConsent {
    hasConsent: boolean;
    timestamp: number;
}

/**
 * Analytics event parameters
 */
export interface AnalyticsEvent {
    name: string;
    parameters?: Record<string, any>;
}

/**
 * Check if user has given analytics consent
 */
export const getAnalyticsConsent = (): AnalyticsConsent | null => {
    try {
        const stored = localStorage.getItem(ANALYTICS_CONSENT_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

/**
 * Check if consent dialog should be shown again
 * Shows again after 1 day if user previously declined
 */
export const shouldShowConsentDialog = (): boolean => {
    const consent = getAnalyticsConsent();

    // No previous consent - show dialog
    if (!consent) {
        return true;
    }

    // User accepted - never show again
    if (consent.hasConsent) {
        return false;
    }

    // User declined - check if 1 day has passed
    const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours
    const timeSinceDecline = Date.now() - consent.timestamp;

    return timeSinceDecline >= oneDayInMs;
};

/**
 * Set analytics consent using gtag's built-in consent management
 */
export const setAnalyticsConsent = (consent: boolean): void => {
    const consentData: AnalyticsConsent = {
        hasConsent: consent,
        timestamp: Date.now(),
    };

    try {
        localStorage.setItem(ANALYTICS_CONSENT_KEY, JSON.stringify(consentData));

        // Use gtag's built-in consent management
        const gtag = (window as any).gtag;
        if (gtag) {
            if (consent) {
                // Grant analytics consent
                gtag('consent', 'update', {
                    analytics_storage: 'granted',
                });
                console.log('Analytics consent granted');
            } else {
                // Deny analytics consent
                gtag('consent', 'update', {
                    analytics_storage: 'denied',
                });
                console.log('Analytics consent denied');
            }
        }
    } catch (error) {
        console.warn('Failed to save analytics consent:', error);
    }
};

/**
 * Track an analytics event
 */
export const trackEvent = (eventName: string, parameters?: Record<string, any>): void => {
    try {
        const gtag = (window as any).gtag;
        if (gtag) {
            gtag('event', eventName, {
                ...parameters,
                timestamp: Date.now(),
            });
        }
    } catch (error) {
        console.warn('Failed to track analytics event:', error);
    }
};

/**
 * Track page view
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
    try {
        const gtag = (window as any).gtag;
        if (gtag) {
            gtag('config', GA_MEASUREMENT_ID, {
                page_path: pagePath,
                page_title: pageTitle,
            });
        }
    } catch (error) {
        console.warn('Failed to track page view:', error);
    }
};

/**
 * Initialize analytics service on app start
 */
export const initializeAnalyticsService = (): void => {
    const consent = getAnalyticsConsent();
    if (consent?.hasConsent) {
        // User previously gave consent, grant it immediately
        setAnalyticsConsent(true);
    }
    // If no consent, gtag will remain in denied state (set in index.html)
};

// Type declarations for gtag
declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}
